import { create } from 'zustand';
import type { PlanningPokerCard, Room } from '../types';

interface PlanningPokerStore {
  cards: PlanningPokerCard[];
  roomInfo: Room | null;
  userVotes: Map<string, string>;
  setCards: (cards: PlanningPokerCard[]) => void;
  setRoomInfo: (room: Room) => void;
  setUserVote: (situationId: string, cardId: string) => void;
  clearUserVote: (situationId: string) => void;
  resetStore: () => void;
}

const defaultCards: PlanningPokerCard[] = [
  { id: 'zero', level: 1, name: '0', description: 'No effort needed' },
  { id: 'half', level: 2, name: '½', description: 'Trivial task' },
  { id: 'one', level: 3, name: '1', description: 'Very small task' },
  { id: 'two', level: 4, name: '2', description: 'Small task' },
  { id: 'three', level: 5, name: '3', description: 'Small-medium task' },
  { id: 'five', level: 6, name: '5', description: 'Medium task' },
  { id: 'eight', level: 7, name: '8', description: 'Large task' },
  { id: 'thirteen', level: 8, name: '13', description: 'Very large task' },
  { id: 'twenty', level: 9, name: '20', description: 'Huge task' },
  { id: 'forty', level: 10, name: '40', description: 'Epic task' },
  { id: 'hundred', level: 11, name: '100', description: 'Massive task' },
  { id: 'question', level: 12, name: '?', description: 'Need more info' },
  { id: 'coffee', level: 13, name: '☕', description: 'Need a break!' },
];

export const usePlanningPokerStore = create<PlanningPokerStore>((set) => ({
  cards: defaultCards,
  roomInfo: null,
  userVotes: new Map(),

  setCards: (cards) => set({ cards }),

  setRoomInfo: (room) => set({ roomInfo: room }),

  setUserVote: (situationId, cardId) =>
    set((state) => {
      const newVotes = new Map(state.userVotes);
      newVotes.set(situationId, cardId);
      return { userVotes: newVotes };
    }),

  clearUserVote: (situationId) =>
    set((state) => {
      const newVotes = new Map(state.userVotes);
      newVotes.delete(situationId);
      return { userVotes: newVotes };
    }),

  resetStore: () =>
    set({
      cards: defaultCards,
      roomInfo: null,
      userVotes: new Map(),
    }),
}));
